import { types, flow, getParent, getRoot, isAlive } from 'mobx-state-tree';
import { PolicySelectedOptions } from '../../../models/BusinessPolicy';
import EbayReturnPolicies from './EbayReturnPolicies';
import EbayPaymentPolicies from './EbayPaymentPolicies';
import EbayShippingPolicies from './EbayShippingPolicies';
import { POLICY_TYPES } from '../../../constants/settings-model';

const BASE_URL = 'business-policy/v1/business-policy-sets';

const PolicySet = types
  .model('PolicySet', {
    code: types.maybe(types.string),
    name: types.optional(types.string, ''),
    shippingPolicyText: types.optional(types.union(types.string, types.null), ''),
    paymentPolicyText: types.optional(types.union(types.string, types.null), ''),
    returnPolicyText: types.optional(types.union(types.string, types.null), ''),
    isDefault: types.optional(types.boolean, true),
    policySelectedOptions: types.optional(PolicySelectedOptions, {}),
    isNew: types.optional(types.boolean, false),
    ebayPaymentPolicies: types.optional(EbayPaymentPolicies, {}),
    ebayReturnPolicies: types.optional(EbayReturnPolicies, {}),
    ebayShippingPolicies: types.optional(EbayShippingPolicies, {}),
    setAsDefault: types.optional(types.boolean, false),
    applyChangesToItems: types.optional(types.boolean, false),
    handlingTimeOptions: types.optional(types.map(types.string), {}),
    domesticLocationsList: types.optional(types.map(types.string), {}),
    countriesList: types.optional(types.map(types.string), {}),
    returnPolicyList: types.optional(types.map(types.string), {}),
  })
  .views(self => ({
    get anyDefaultSet() {
      return !!getParent(self, 2).defaultPolicy;
    },
    get policyNormalizedOptions() {
      const { policySetOptions } = getParent(self, 2);
      const { shipping, payment, returns } = policySetOptions;
      if (self.policySelectedOptions) {
        const {
          paymentPolicyCode,
          returnsPolicyCode,
          shippingPolicyCode,
        } = self.policySelectedOptions;
        return {
          shipping: shipping.find(o => o.code === shippingPolicyCode),
          payment: payment.find(o => o.code === paymentPolicyCode),
          returns: returns.find(o => o.code === returnsPolicyCode),
        };
      }
      return null;
    },
    get listing() {
      return getParent(self, 3).listing;
    },
    get destinationMarketplaceId() {
      return getParent(self, 3).markets.defaultDestination.id;
    },
    get sourceMarketplaceId() {
      return getParent(self, 3).markets.defaultSource.id;
    },
    get sellerAccountCode() {
      return getParent(self, 3).markets.defaultSellersAccount.id;
    },
    get accountCode() {
      return getRoot(self).uiStore.sessionStore.accountCode;
    },
    get transport5007() {
      return getParent(self, 2).transport5007;
    },
  }))
  .actions(self => ({
    afterAttach() {
      try {
        self.fetchHandlingTimeOptions().then(({ data }) => {
          if (isAlive(self)) {
            self.setHandlingTimeOptions(data);
          }
        });
        self.fetchDomesticLocationsList().then(({ data }) => {
          if (isAlive(self)) {
            self.setDomesticLocationsList(data);
          }
        });
        self.fetchCountriesList().then(({ data }) => {
          if (isAlive(self)) {
            self.setCountriesList(data);
          }
        });
        self.fetchReturnPolicyList().then(({ data }) => {
          if (isAlive(self)) {
            self.setReturnPolicyList(data);
          }
        });
      } catch (err) {
        console.log(err);
      }
    },
    setHandlingTimeOptions(value) {
      self.handlingTimeOptions = value;
    },
    setDomesticLocationsList(value) {
      self.domesticLocationsList = value;
    },
    setCountriesList(value) {
      self.countriesList = value;
    },
    setReturnPolicyList(value) {
      self.returnPolicyList = value;
    },
    fetchHandlingTimeOptions() {
      const url = 'business-policy/v1/settings/handling-days';
      const params = { marketplaceId: self.destinationMarketplaceId };
      return self.transport5007.fetch(url, params);
    },
    fetchDomesticLocationsList() {
      const url = 'business-policy/v1/settings/domestic-locations';
      const params = { marketplaceId: self.destinationMarketplaceId };
      return self.transport5007.fetch(url, params);
    },
    fetchCountriesList() {
      const url = 'business-policy/v1/settings/countries';
      const params = { marketplaceId: self.destinationMarketplaceId };
      return self.transport5007.fetch(url, params);
    },
    fetchReturnPolicyList() {
      const url = 'business-policy/v1/settings/returns';
      const params = { marketplaceId: self.destinationMarketplaceId };
      return self.transport5007.fetch(url, params);
    },
    resetShippingForm() {
      self.ebayShippingPolicies = EbayShippingPolicies.create();
    },
    setPolicy(e, type) {
      if (self.policySelectedOptions) {
        self.policySelectedOptions[POLICY_TYPES[type]] = e.key;
      }
    },
    setPolicyName(e) {
      self.name = e.target.value;
      self.policySelectedOptions.name = e.target.value;
    },
    toggleSetAsDefault(e) {
      self.setAsDefault = e.target.checked;
    },
    toggleApplyToItems(e) {
      self.applyChangesToItems = e.target.checked;
    },
    fetchPolicySelectedOptions: flow(function* fetchPolicySelectedOptions() {
      try {
        const businessPolicySetCode = self.code;
        if (businessPolicySetCode) {
          const url = `/${BASE_URL}/${businessPolicySetCode}?currency=${
            getRoot(self).uiStore.settingsStore.userProfile.defaultCurrencyId
          }`;
          const { data } = yield self.transport5007.fetch(url, {});
          self.policySelectedOptions = data;
        }
      } catch (err) {
        console.log(err);
      }
    }),
    savePolicyNewOptions: flow(function* savePolicyNewOptions() {
      try {
        const selectedOptions = self.policySelectedOptions;
        yield self.transport5007.update(BASE_URL, selectedOptions);
      } catch (err) {
        console.log(err);
      }
    }),
    createNewPolicySet: flow(function* createNewPolicySet(
      selectedAccountCode,
      selectedSrcId,
      selectedDestId,
    ) {
      try {
        const data = {
          sourceMarketplaceId: selectedSrcId || self.sourceMarketplaceId,
          ...self.policySelectedOptions,
          setAsDefault: self.setAsDefault,
          applyChangesToItems: self.applyChangesToItems,
          destinationMarketplaceId: selectedDestId,
        };
        const headers = {
          'Seller-Account-Id': selectedAccountCode || self.sellerAccountCode,
        };
        yield self.transport5007.post(BASE_URL, data, {}, headers);
      } catch (err) {
        console.log(err);
      }
    }),
    toggleDefaultPolicy: flow(function* toggleDefaultPolicy() {
      try {
        getParent(self, 2).toggleDefaultPolicy();
        self.isDefault = true;
        const url = `${BASE_URL}/${self.code}/set-default`;
        const headers = {
          'Seller-Account-Id': self.sellerAccountCode,
        };
        yield self.transport5007.update(
          url,
          {
            sourceMarketplaceId: self.marketplaceId,
          },
          {},
          headers,
        );
      } catch (err) {
        console.log(err);
      }
    }),
    deletePolicySet: flow(function* deletePolicySet() {
      try {
        const url = `${BASE_URL}/${self.code}`;
        const headers = {
          'Seller-Account-Id': self.sellerAccountCode,
        };
        yield self.transport5007.delete(url, {}, {}, headers);
        getParent(self, 2).deletePolicy(self.code);
      } catch (err) {
        console.log(err);
      }
    }),
    fetchShippingPolicyData: flow(function* fetchShippingPolicyData() {
      try {
        const code = self.policySelectedOptions.shippingPolicyCode;
        const url = `business-policy/v1/shipping-policies/get-shipping-policy`;
        const body = {
          accountId: self.accountCode,
          sellerAccountId: self.sellerAccountCode,
          businessPolicyId: code,
          destinationMarketplaceId: getParent(self).selectedDestId,
          currency: getRoot(self).uiStore.settingsStore.userProfile.defaultCurrencyId,
        };
        const { data } = yield self.transport5007.post(url, body, {});
        self.ebayShippingPolicies = data;
      } catch (err) {
        console.log(err);
      }
    }),
    getEbayShippingForm(isCreateAction) {
      if (!isCreateAction) {
        self.fetchShippingPolicyData();
      }
      return self.ebayShippingPolicies;
    },
    getEbayReturnsForm(isCreateAction) {
      if (isCreateAction) {
        self.ebayReturnPolicies.name = '';
        self.ebayReturnPolicies.domesticDays = '';
        self.ebayReturnPolicies.internationalDays = '';
      } else {
        const { returns } = self.policyNormalizedOptions;
        self.ebayReturnPolicies.name = returns.name;
        self.ebayReturnPolicies.domesticDays = self.returnPolicyList.get(
          returns.domesticDays.toString(),
        );
        self.ebayReturnPolicies.internationalDays = self.returnPolicyList.get(
          returns.internationalDays.toString(),
        );
      }
      return self.ebayReturnPolicies;
    },
    getEbayPaymentForm(isCreateAction) {
      if (isCreateAction) {
        self.ebayPaymentPolicies.name = '';
        self.ebayPaymentPolicies.name = '';
        self.ebayPaymentPolicies.account = '';
      } else {
        self.ebayPaymentPolicies.name = self.policyNormalizedOptions.payment.name;
        self.ebayPaymentPolicies.account = self.policyNormalizedOptions.payment.details || '';
      }
      return self.ebayPaymentPolicies;
    },
  }));

export default PolicySet;
